import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';

const AVVERTIMENTO =
    'Questa password è mostrata una volta sola e non è recuperabile: Keycloak ne conserva ' +
    'solo l’impronta. Va consegnata alla persona fuori da Automate. Se si perde, si rigenera ' +
    'dal menu azioni della sua riga.';

const OBBLIGO_CAMBIO = 'Al primo accesso Keycloak chiede di cambiarla.';

/**
 * Mostra la password temporanea appena generata (spec §6/D7).
 *
 * <b>E' l'unico punto in cui esiste fuori da Keycloak.</b> Non finisce nel registro di audit
 * ne' nei log — ne' lato backend ne' qui — e non resta in stato oltre la chiusura del
 * dialogo, che e' il motivo per cui il chiamante azzera il valore su `onClose`.
 */
export default function PasswordTemporaneaDialog({ esito, onClose }) {
    const [copiata, setCopiata] = useState(false);

    const copia = () => {
        // `navigator.clipboard` non c'e' in contesti non sicuri: il fallimento non deve
        // impedire di leggere la password, che e' comunque a schermo.
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(esito.temporaryPassword).then(
                () => setCopiata(true),
                () => setCopiata(false),
            );
        }
    };

    return (
        <Dialog open={Boolean(esito)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Password temporanea</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="warning">{AVVERTIMENTO}</Alert>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            variant="body1"
                            data-testid="password-temporanea"
                            sx={{
                                fontFamily: 'monospace',
                                px: 1.5,
                                py: 1,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                flexGrow: 1,
                                wordBreak: 'break-all',
                            }}
                        >
                            {esito?.temporaryPassword}
                        </Typography>
                        <Tooltip title={copiata ? 'Copiata' : 'Copia'}>
                            <IconButton onClick={copia} aria-label="Copia la password">
                                <CopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {OBBLIGO_CAMBIO}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Ho annotato la password
                </Button>
            </DialogActions>
        </Dialog>
    );
}

PasswordTemporaneaDialog.propTypes = {
    // `null` quando non c'e' nulla da mostrare: il dialogo non tiene la password in stato
    // proprio, cosi' chiudendolo sparisce davvero.
    esito: PropTypes.shape({
        sub: PropTypes.string,
        temporaryPassword: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

PasswordTemporaneaDialog.defaultProps = {
    esito: null,
};
